// The activity snapshot on a horse card in the list.
//
// Three labeled lines, each meaning exactly one kind of thing:
//
//   NEXT     the next appointment — a lesson, or a show it travels to
//   DUE      the next bit of maintenance falling due
//   LAST     the last service actually performed
//
// One color system runs through the card, so a state is the same color
// wherever it appears and a grid of thirty of these can be read down rather
// than across:
//
//   green    something planned
//   amber    maintenance coming up
//   rose     late
//   sky      away from the barn today
//   gray     already happened
//
// A row with nothing in it isn't rendered, so a quiet horse stays quiet and the
// cards don't fill with "—". That matters more than alignment across the grid:
// a column of dashes reads as data, and there is no data. The horse's own page
// does the opposite and says "Nothing scheduled" out loud, because there you
// asked about that horse specifically.
//
// The rules for what counts as next, due or away live in lib/horseActivity, so
// this and the detail page can't drift apart. Everything comes from
// GET /horses/activity, a separate request from the list itself — the names and
// stalls paint immediately and this fills in behind them.

import {
  label,
  when,
  overdueFor,
  isAwayNow,
  nextAppointment,
  dueSoon,
} from '../../lib/horseActivity';

function Row({ tag, tone, children }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      {/* Color before the word, so the kind of thing registers before the
          label is read — which is the whole job of a bar in a list. */}
      <span className={'w-1 h-3.5 rounded-full shrink-0 ' + tone} aria-hidden="true" />
      <span className="text-[10px] font-medium tracking-wider text-ink-400 w-9 shrink-0 uppercase">
        {tag}
      </span>
      <span className="text-xs text-ink-600 truncate">{children}</span>
    </div>
  );
}

/**
 * The status lozenges, right-aligned on their own line.
 *
 * They started beside the name and had to move: a card is 372px in the
 * three-column grid, and "At Desert Circuit III" next to a photo left about
 * 120px for the horse, which clipped the breed line on every card whether it
 * had a status or not. On its own line the lozenge gets the full width, the
 * identity above it gets the full width, and nothing truncates that didn't
 * need to.
 */
export function HorseStatus({ snapshot, today }) {
  if (!snapshot || !today) return null;
  const { overdue } = snapshot;
  const away = isAwayNow(snapshot, today);
  if (!overdue && !away) return null;

  return (
    <div className="flex flex-wrap justify-end gap-1 mt-2">
      {overdue && (
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 text-[11px] font-medium leading-none max-w-full">
          <span className="truncate">{label(overdue.type)} overdue</span>
          {/* The number is the point: a week late and four months late are the
              same word and very different problems. */}
          <span className="tabular-nums font-normal shrink-0">
            {overdueFor(today, overdue.date)}
          </span>
        </span>
      )}
      {away && (
        <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-800 border border-sky-200 px-2 py-0.5 text-[11px] font-medium leading-none max-w-full">
          <span className="truncate">At {snapshot.event.title}</span>
        </span>
      )}
    </div>
  );
}

export default function HorseActivity({ snapshot, today }) {
  if (!snapshot || !today) return null;

  const { lastService } = snapshot;
  const next = nextAppointment(snapshot, today);
  const due = dueSoon(snapshot, today);

  // The lozenges live in the card header now, so a snapshot carrying only a
  // status has nothing left to draw down here.
  if (!next && !due && !lastService) return null;

  return (
    <div className="mt-3 pt-3 border-t border-ink-100 flex flex-col gap-1">
      {next && (
        <Row tag="Next" tone="bg-hunter">
          <span className="text-ink-800 font-medium">{when(today, next.date)}</span>
          {[next.time, next.detail]
            .filter(Boolean)
            .map((part) => ` · ${part}`)
            .join('')}
        </Row>
      )}

      {due && (
        <Row tag="Due" tone="bg-amber-500">
          {label(due.type)} · {when(today, due.date)}
        </Row>
      )}

      {lastService && (
        <Row tag="Last" tone="bg-ink-300">
          {label(lastService.type)} · {when(today, lastService.date)}
          {lastService.providerName ? ` · ${lastService.providerName}` : ''}
        </Row>
      )}
    </div>
  );
}
